/**
 * Reorder or Delete Slides Tool
 * 
 * Tool for reordering and deleting slides in PowerPoint presentations.
 * Uses a user approval pattern similar to UserInterviewTool:
 * - Move operations are auto-approved (just shown to user)
 * - Delete operations require explicit user approval
 * 
 * After execution, syncs changes to IndexedDB and backend.
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import { 
    ReorderOrDeleteSlidesParamsSchema,
    type ReorderOrDeleteSlidesParams,
    type ReorderOrDeleteSlidesResponse,
    type SlideOperation,
    type PendingOperation,
    type OperationResult,
    type ReorderOrDeleteApprovalData,
} from "./reorderOrDeleteSlides.schema";
import { LiveTrackingStorage, SlideTrackingSnapshot } from "../../../../../services/tracking/liveTrackingStorage";
import { BackendClient } from "../../../../../services/communication/backendClient";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";
import { slideId as normalizeSlideId } from "../../../../../utils/idNormalizer";

// ═══════════════════════════════════════════════════════════════════════════
// PENDING APPROVAL STATE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Module-level state for tracking pending approval.
 * Only one approval session can be active at a time.
 */
interface PendingApproval {
    id: string;
    documentId: string;
    operations: PendingOperation[];
    resolve: (response: ReorderOrDeleteSlidesResponse) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
    createdAt: number;
}

let pendingApproval: PendingApproval | null = null;

// 5 minute timeout
const APPROVAL_TIMEOUT_MS = 5 * 60 * 1000;

// ═══════════════════════════════════════════════════════════════════════════
// TOOL IMPLEMENTATION
// ═══════════════════════════════════════════════════════════════════════════

export class ReorderOrDeleteSlidesTool extends ToolBase<ReorderOrDeleteSlidesParams, ReorderOrDeleteSlidesResponse> {
    public readonly name = "reorderOrDeleteSlides";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = `### reorderOrDeleteSlides
Reorder or delete slides in the presentation.

**When to use:**
- Moving slides to a different position
- Deleting unwanted slides
- Reorganizing presentation structure

**⚠️ CRITICAL: Operations are sequential - indices SHIFT after each operation!**
- Moving slide 5 → position 2: slides 2,3,4 shift to become 3,4,5
- Deleting slide 3: slides 4,5,6... shift to become 3,4,5...
- Use slideId (more reliable) instead of slideIndex when possible

**Approval required:**
- Move operations are auto-approved (shown to user)
- Delete operations require explicit user approval

\`\`\`json
{
  "operations": [
    { "slideIndex": 5, "action": "move", "newIndex": 2, "reason": "Move intro to beginning" },
    { "slideId": "slide_257", "action": "delete", "reason": "Duplicate of slide 3" }
  ]
}
\`\`\``;
    
    private storage: LiveTrackingStorage;
    private backendClient: BackendClient;
    
    // Streaming config - operations stream in as array items
    fieldsToCheck = [
        { name: "operations", type: "array" as const, list_status: true, order: 1 },
    ];
    
    listsToCheck = [
        { 
            name: "operations", 
            type: "array" as const, 
            item_type: "object" as const,
            list_status: true, 
            order: 1,
            check_sub_field: "action",  // Operation is ready when action is present
        },
    ];

    constructor() {
        super();
        this.storage = new LiveTrackingStorage();
        this.backendClient = new BackendClient();
        this.description = new ToolDescription(
            "reorderOrDeleteSlides",
            `Reorder or delete slides in the presentation.

Takes a list of operations executed IN ORDER. Each operation targets a slide by ID or index.

⚠️ IMPORTANT: Operations are sequential - indices SHIFT after each operation!
- Moving slide 5 → position 2: slides 2,3,4 shift to become 3,4,5
- Deleting slide 3: slides 4,5,6... shift to become 3,4,5...
- Use slideId (more reliable) instead of slideIndex when possible

Move operations are shown to user but auto-approved.
Delete operations require explicit user approval before execution.

Example:
{
  "operations": [
    { "slideIndex": 5, "action": "move", "newIndex": 2, "reason": "Move intro to beginning" },
    { "slideId": "slide_257", "action": "delete", "reason": "Duplicate of slide 3" }
  ]
}`,
            ReorderOrDeleteSlidesParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<ReorderOrDeleteSlidesResponse> {
        const parseResult = safeParseToolParams(ReorderOrDeleteSlidesParamsSchema, parameters);
        
        if (!parseResult.success) {
            console.error('[ReorderOrDeleteSlides] Invalid parameters:', parseResult.error);
            return {
                results: [],
                status: "cancelled",
                summary: `Invalid parameters: ${parseResult.error}`,
            };
        }
        
        const params = parseResult.data;
        const approvalId = `approval_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        
        // ARCHITECTURE FIX: Use anchored documentId from conversation context if available.
        // The parent agent injects documentId into parameters, which comes from the conversation's
        // presentationId (set at conversation spawn time). This ensures the tool operates on
        // the correct deck even if the user switches presentations mid-conversation.
        // Only fall back to getOrCreatedocumentId() for backwards compatibility.
        let documentId: string;
        const injecteddocumentId = (parameters as Record<string, unknown>)?.documentId as string | undefined;
        if (injecteddocumentId) {
            documentId = injecteddocumentId;
            console.log('[ReorderOrDeleteSlides] 🔧 Starting with anchored deck ID:', {
                operationsCount: params.operations.length,
                documentId
            });
        } else {
            try {
                documentId = await getOrCreatedocumentId();
                console.log('[ReorderOrDeleteSlides] 🔧 Starting with fallback deck ID:', {
                    operationsCount: params.operations.length,
                    documentId
                });
                console.warn('[ReorderOrDeleteSlides] ⚠️ No anchored documentId in parameters - using live lookup');
            } catch (error) {
                console.error('[ReorderOrDeleteSlides] Failed to get deck ID:', error);
                return {
                    results: [],
                    status: "cancelled",
                    summary: "No active presentation - cannot modify slides.",
                };
            }
        }
        
        // Initialize storage
        await this.storage.initialize();
        
        // Resolve all operations to slide IDs and build pending operations
        const pendingOps = await this.resolveOperations(documentId, params.operations);
        
        if (pendingOps.length === 0) {
            return {
                results: [],
                status: "cancelled",
                summary: "No valid operations to perform.",
            };
        }
        
        // Check if any deletions require approval
        const deletions = pendingOps.filter(op => op.operation.action === "delete");
        const hasDeletes = deletions.length > 0;
        
        if (!hasDeletes) {
            // No deletions - execute all moves immediately
            console.log('[ReorderOrDeleteSlides] No deletions - executing moves immediately');
            return this.executeOperations(documentId, pendingOps);
        }
        
        // Has deletions - need user approval
        console.log('[ReorderOrDeleteSlides] Deletions require approval:', {
            deletionCount: deletions.length
        });
        
        return new Promise<ReorderOrDeleteSlidesResponse>((resolve, reject) => {
            const timeoutId = setTimeout(() => {
                if (pendingApproval && pendingApproval.id === approvalId) {
                    console.log('[ReorderOrDeleteSlides] Approval timed out:', {
                        approvalId
                    });
                    pendingApproval = null;
                    resolve({
                        results: [],
                        status: "timeout",
                        summary: "Approval timed out. No changes were made.",
                    });
                }
            }, APPROVAL_TIMEOUT_MS);
            
            pendingApproval = {
                id: approvalId,
                documentId,
                operations: pendingOps,
                resolve,
                reject,
                timeoutId,
                createdAt: Date.now(),
            };
            
            console.log('[ReorderOrDeleteSlides] Waiting for user approval:', {
                approvalId,
                operationsCount: pendingOps.length
            });
        });
    }

    /**
     * Resolve operations to normalized slide IDs and gather preview data
     */
    private async resolveOperations(
        documentId: string, 
        operations: SlideOperation[]
    ): Promise<PendingOperation[]> {
        const pendingOps: PendingOperation[] = [];
        
        await PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            slides.load('items');
            await context.sync();
            
            // Load all slide IDs
            for (const slide of slides.items) {
                slide.load('id');
            }
            await context.sync();
            
            // Build maps for resolution
            const slideIdByIndex = new Map<number, string>();
            const slideIndexById = new Map<string, number>();
            
            slides.items.forEach((slide, idx) => {
                const cleanId = normalizeSlideId(slide.id);
                slideIdByIndex.set(idx + 1, cleanId);  // 1-based
                slideIndexById.set(cleanId, idx + 1);
            });
            
            console.log('[ReorderOrDeleteSlides] Resolving operations:', {
                operationsCount: operations.length,
                totalSlides: slides.items.length
            });
            
            // Resolve each operation
            for (let i = 0; i < operations.length; i++) {
                const op = operations[i];
                let resolvedSlideId: string;
                let currentIndex: number;
                
                if (op.slideId) {
                    resolvedSlideId = normalizeSlideId(op.slideId);
                    currentIndex = slideIndexById.get(resolvedSlideId) || -1;
                } else if (op.slideIndex) {
                    resolvedSlideId = slideIdByIndex.get(op.slideIndex) || '';
                    currentIndex = op.slideIndex;
                } else {
                    console.warn(`[ReorderOrDeleteSlides] Operation ${i} has no slideId or slideIndex - skipping`);
                    continue;
                }
                
                if (!resolvedSlideId || currentIndex < 1) {
                    console.warn(`[ReorderOrDeleteSlides] Could not resolve slide for operation ${i}`);
                    continue;
                }
                
                // Get slide preview from IndexedDB storage
                let slideTitle: string | undefined;
                let slideImage: string | undefined;
                
                try {
                    const snapshot = await this.storage.getSnapshot(documentId, resolvedSlideId);
                    slideTitle = snapshot?.shortDescription;
                    slideImage = snapshot?.imageBase64;
                } catch (e) {
                    console.warn(`[ReorderOrDeleteSlides] Could not get snapshot for ${resolvedSlideId}`);
                }
                
                pendingOps.push({
                    index: i,
                    operation: op,
                    resolvedSlideId,
                    currentIndex,
                    slideTitle,
                    slideImage,
                    requiresApproval: op.action === "delete",
                    approved: op.action === "move" ? true : undefined,  // Auto-approve moves
                });
                
                console.log(`[ReorderOrDeleteSlides] Resolved op ${i}: ${op.action} slide ${resolvedSlideId} (index ${currentIndex})`);
            }
        });
        
        return pendingOps;
    }

    /**
     * Execute approved operations and sync to IndexedDB + backend
     */
    private async executeOperations(
        documentId: string,
        operations: PendingOperation[]
    ): Promise<ReorderOrDeleteSlidesResponse> {
        const results: OperationResult[] = [];
        const rejected: SlideOperation[] = [];
        const deletedSlideIds: string[] = [];
        
        console.log('[ReorderOrDeleteSlides] Executing operations:', {
            count: operations.length
        });
        
        await PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            
            for (const pendingOp of operations) {
                // Skip rejected deletions
                if (pendingOp.requiresApproval && pendingOp.approved === false) {
                    console.log(`[ReorderOrDeleteSlides] Skipping rejected deletion: ${pendingOp.resolvedSlideId}`);
                    rejected.push(pendingOp.operation);
                    continue;
                }
                
                try {
                    slides.load('items');
                    await context.sync();
                    
                    // Find the slide by ID (must re-find each time as indices shift)
                    let targetSlide: PowerPoint.Slide | null = null;
                    let currentIndex = -1;
                    
                    for (let i = 0; i < slides.items.length; i++) {
                        const slide = slides.items[i];
                        slide.load('id');
                        await context.sync();
                        
                        if (normalizeSlideId(slide.id) === pendingOp.resolvedSlideId) {
                            targetSlide = slide;
                            currentIndex = i;
                            break;
                        }
                    }
                    
                    if (!targetSlide) {
                        console.warn(`[ReorderOrDeleteSlides] Slide ${pendingOp.resolvedSlideId} not found`);
                        results.push({
                            slideId: pendingOp.resolvedSlideId,
                            action: pendingOp.operation.action,
                            success: false,
                            error: "Slide not found (may have been deleted)",
                        });
                        continue;
                    }
                    
                    if (pendingOp.operation.action === "delete") {
                        console.log('[ReorderOrDeleteSlides] 🗑️ Deleting slide:', {
                            slideId: pendingOp.resolvedSlideId,
                            currentIndex: currentIndex + 1
                        });
                        targetSlide.delete();
                        await context.sync();
                        
                        deletedSlideIds.push(pendingOp.resolvedSlideId);
                        
                        results.push({
                            slideId: pendingOp.resolvedSlideId,
                            action: "delete",
                            success: true,
                        });
                        
                    } else if (pendingOp.operation.action === "move") {
                        const destIndex = (pendingOp.operation.newIndex || 1) - 1;  // Convert to 0-based
                        console.log(`[ReorderOrDeleteSlides] 🔄 Moving slide ${pendingOp.resolvedSlideId} from ${currentIndex + 1} to ${destIndex + 1}`);
                        targetSlide.moveTo(destIndex);
                        await context.sync();
                        
                        results.push({
                            slideId: pendingOp.resolvedSlideId,
                            action: "move",
                            success: true,
                            finalIndex: destIndex + 1,
                        });
                    }
                    
                } catch (error) {
                    console.error(`[ReorderOrDeleteSlides] Error executing operation:`, error);
                    results.push({
                        slideId: pendingOp.resolvedSlideId,
                        action: pendingOp.operation.action,
                        success: false,
                        error: error instanceof Error ? error.message : String(error),
                    });
                }
            }
        });
        
        // ═══════════════════════════════════════════════════════════════════════
        // SYNC INDEXEDDB AND BACKEND
        // ═══════════════════════════════════════════════════════════════════════
        
        await this.syncAfterOperations(documentId, results, deletedSlideIds);
        
        // Build summary
        const moves = results.filter(r => r.action === "move" && r.success).length;
        const deletes = results.filter(r => r.action === "delete" && r.success).length;
        const failures = results.filter(r => !r.success).length;
        
        const summaryParts = [];
        if (moves > 0) summaryParts.push(`${moves} slide(s) moved`);
        if (deletes > 0) summaryParts.push(`${deletes} slide(s) deleted`);
        if (rejected.length > 0) summaryParts.push(`${rejected.length} deletion(s) rejected by user`);
        if (failures > 0) summaryParts.push(`${failures} operation(s) failed`);
        
        const status = rejected.length > 0 ? "partial" : "completed";
        const summary = summaryParts.join(", ") || "No changes made";
        
        console.log('[ReorderOrDeleteSlides] ✅ Complete:', {
            summary,
            moves,
            deletes,
            rejected: rejected.length,
            failures
        });
        
        return {
            results,
            status,
            summary,
            rejectedOperations: rejected.length > 0 ? rejected : undefined,
        };
    }

    /**
     * Sync changes to IndexedDB and backend after operations
     */
    private async syncAfterOperations(
        documentId: string,
        results: OperationResult[],
        deletedSlideIds: string[]
    ): Promise<void> {
        // 1. Delete snapshots for deleted slides from IndexedDB
        if (deletedSlideIds.length > 0) {
            for (const slideId of deletedSlideIds) {
                try {
                    await this.storage.deleteSnapshot(documentId, slideId);
                } catch (e) {
                    console.warn('[ReorderOrDeleteSlides] Failed to delete snapshot:', { slideId, error: e });
                }
            }
            
            // 2. Mark slides as unavailable in backend
            try {
                const result = await this.backendClient.markSlidesUnavailable(documentId, deletedSlideIds);
                console.log('[ReorderOrDeleteSlides] ✅ Marked slides unavailable:', {
                    markedCount: result.marked_unavailable,
                    totalRequested: deletedSlideIds.length,
                    documentId
                });
            } catch (e) {
                console.warn(`[ReorderOrDeleteSlides] ⚠️ Failed to mark slides unavailable in backend:`, e);
            }
        }
        
        // 3. Update deck state with new slide order
        const hasMoves = results.some(r => r.action === "move" && r.success);
        if (hasMoves || deletedSlideIds.length > 0) {
            await this.syncSlideOrder(documentId);
        }
    }

    /**
     * Sync the current slide order to IndexedDB and backend
     */
    private async syncSlideOrder(documentId: string): Promise<void> {
        await PowerPoint.run(async (context) => {
            const slides = context.presentation.slides;
            slides.load('items');
            await context.sync();
            
            // Load all slide IDs
            for (const slide of slides.items) {
                slide.load('id');
            }
            await context.sync();
            
            // Build current order
            const currentSlideIds: string[] = [];
            const mappings: Array<{ slideId: string; slideIndex: number }> = [];
            
            for (let i = 0; i < slides.items.length; i++) {
                const cleanId = normalizeSlideId(slides.items[i].id);
                currentSlideIds.push(cleanId);
                mappings.push({ slideId: cleanId, slideIndex: i });
            }
            
            console.log(`[ReorderOrDeleteSlides] New slide order: ${currentSlideIds.length} slides`);
            
            // Update IndexedDB snapshots with new indexes
            for (const mapping of mappings) {
                try {
                    const snapshot = await this.storage.getSnapshot(documentId, mapping.slideId);
                    if (snapshot && snapshot.slideIndex !== mapping.slideIndex) {
                        await this.storage.saveSnapshot({
                            ...snapshot,
                            slideIndex: mapping.slideIndex,
                            timestamp: Date.now(),
                        });
                    }
                } catch (e) {
                    console.warn(`[ReorderOrDeleteSlides] Failed to update index for ${mapping.slideId}:`, e);
                }
            }
            
            // Update deck state with new slide IDs
            try {
                const deckState = await this.storage.getDeckState(documentId);
                if (deckState) {
                    await this.storage.saveDeckState({
                        ...deckState,
                        slideIds: currentSlideIds,
                        lastStructureCheck: Date.now(),
                    });
                    console.log('[ReorderOrDeleteSlides] ✅ Updated deck state:', {
                        slideCount: currentSlideIds.length,
                        documentId
                    });
                }
            } catch (e) {
                console.warn('[ReorderOrDeleteSlides] Failed to update deck state:', e);
            }
            
            // Sync indexes to backend
            try {
                const result = await this.backendClient.updateSlideIndexes(documentId, mappings);
                console.log('[ReorderOrDeleteSlides] ✅ Updated slide indexes:', {
                    updatedCount: result.updated_count,
                    totalRequested: mappings.length,
                    documentId
                });
            } catch (e) {
                console.warn(`[ReorderOrDeleteSlides] ⚠️ Failed to sync indexes to backend:`, e);
            }
        });
    }

    // ═══════════════════════════════════════════════════════════════════════
    // STATIC METHODS (called by UI)
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Called by UI when user approves/rejects operations
     * @param approvalDecisions - Map of operation index -> approved (true/false)
     */
    public static async submitApproval(approvalDecisions: Map<number, boolean>): Promise<void> {
        if (!pendingApproval) {
            console.warn('[ReorderOrDeleteSlides] submitApproval called but no pending approval');
            return;
        }
        
        console.log('[ReorderOrDeleteSlides] Processing approval decisions:', {
            operationsCount: approvalDecisions.size
        });
        clearTimeout(pendingApproval.timeoutId);
        
        // Update approval status on pending operations
        for (const op of pendingApproval.operations) {
            if (op.requiresApproval) {
                op.approved = approvalDecisions.get(op.index) ?? false;
                console.log('[ReorderOrDeleteSlides] Operation decision:', {
                    index: op.index,
                    slideId: op.resolvedSlideId,
                    approved: op.approved ? 'APPROVED' : 'REJECTED'
                });
            }
        }
        
        const documentId = pendingApproval.documentId;
        const operations = pendingApproval.operations;
        const resolve = pendingApproval.resolve;
        
        // Clear pending state before executing (to avoid re-entry)
        pendingApproval = null;
        
        // Execute operations
        const tool = new ReorderOrDeleteSlidesTool();
        await tool.storage.initialize();
        const response = await tool.executeOperations(documentId, operations);
        
        resolve(response);
    }
    
    /**
     * Called when user cancels the entire operation
     */
    public static cancelApproval(reason?: string): void {
        if (!pendingApproval) return;
        
        console.log('[ReorderOrDeleteSlides] Approval cancelled:', {
            reason: reason || 'user action'
        });
        clearTimeout(pendingApproval.timeoutId);
        const resolve = pendingApproval.resolve;
        pendingApproval = null;
        
        resolve({
            results: [],
            status: "cancelled",
            summary: reason || "User cancelled the operation. No changes were made.",
        });
    }
    
    /**
     * Check if there's a pending approval
     */
    public static hasPendingApproval(): boolean {
        return pendingApproval !== null;
    }
    
    /**
     * Get pending approval data for UI rendering
     */
    public static getPendingApproval(): ReorderOrDeleteApprovalData | null {
        if (!pendingApproval) return null;
        return {
            id: pendingApproval.id,
            operations: pendingApproval.operations,
            currentOperationIndex: 0,
            status: 'pending',
        };
    }
    
    /**
     * Get the approval ID (for component keying)
     */
    public static getPendingApprovalId(): string | null {
        return pendingApproval?.id ?? null;
    }
}

export default ReorderOrDeleteSlidesTool;

